/** Initial state for Generic Logistics RFQ (UI + draft/submit payload shape). */

export const RFQ_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'under_review', label: 'Under review' },
  { value: 'closed', label: 'Closed' },
];

export const WEIGHT_UNIT_OPTIONS = ['kg', 'lb', 'ton', 'mt'];
/** Total weight / display units (cargo metrics row). */
export const CARGO_WEIGHT_UNIT_OPTIONS = ['KG', 'LB', 'MT', 'TON'];
/** Total volume units (cargo metrics row). */
export const CARGO_VOLUME_UNIT_OPTIONS = ['CBM', 'CFT', 'cu.m'];
export const VALUE_CURRENCY_OPTIONS = ['INR', 'USD', 'EUR', 'GBP'];

/** Additional services — 4-column grid (Service Requirements section). */
export const ADDITIONAL_SERVICE_OPTIONS = [
  { key: 'door_to_door_delivery', label: 'Door-to-Door Delivery' },
  { key: 'real_time_tracking', label: 'Real-time Tracking' },
  { key: 'pick_pack', label: 'Pick & Pack' },
  { key: 'warehouse_storage', label: 'Warehouse Storage' },
  { key: 'documentation_support', label: 'Documentation Support' },
  { key: 'labeling_marking', label: 'Labeling & Marking' },
  { key: 'loading_unloading', label: 'Loading/Unloading' },
  { key: 'quality_inspection', label: 'Quality Inspection' },
  { key: 'palletization', label: 'Palletization' },
  { key: 'packaging_service', label: 'Packaging Service' },
  { key: 'express_clearance', label: 'Express Clearance' },
  { key: 'fumigation', label: 'Fumigation' },
];

export const INCOTERM_OPTIONS = ['EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP', 'FAS', 'FOB', 'CFR', 'CIF'];

/** Commercial Terms & Volume — quote validity dropdown. */
export const QUOTE_VALIDITY_PERIOD_OPTIONS = ['', '7 days', '14 days', '30 days', '60 days', '90 days', '120 days'];

/** Shipment frequency (commercial section). */
export const COMMERCIAL_SHIPMENT_FREQUENCY_OPTIONS = ['', 'One-time', 'Weekly', 'Bi-weekly', 'Monthly', 'Quarterly', 'Ad hoc'];

/** Contract duration dropdown. */
export const CONTRACT_DURATION_OPTIONS = ['', '3 months', '6 months', '12 months', '24 months', '36 months', 'Ongoing / open-ended'];

export const PREFERRED_PAYMENT_TERM_OPTIONS = [
  { key: 'advance_payment', label: 'Advance Payment' },
  { key: 'cod', label: 'COD (Cash on Delivery)' },
  { key: 'net_30', label: 'Net 30' },
  { key: 'net_45', label: 'Net 45' },
  { key: 'net_60', label: 'Net 60' },
  { key: 'letter_of_credit', label: 'Letter of Credit' },
  { key: 'escrow', label: 'Escrow' },
  { key: 'milestone_based', label: 'Milestone Based' },
];

/** Legacy checkbox keys → display labels (draft migration only). */
export function legacyPreferredPaymentTermsObjectToLabels(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
  return PREFERRED_PAYMENT_TERM_OPTIONS.filter((o) => Boolean(obj[o.key])).map((o) => o.label);
}

/**
 * Shipment type dropdown options — list changes with selected transport mode (road / ocean / rail / air / multimodal).
 * Value is stored in form field `modeDetail`.
 */
export const SHIPMENT_TYPES_BY_TRANSPORT_MODE = {
  road: ['FTL', 'LTL', 'PTL', 'Partial load', 'Parcel / courier', 'Express road', 'Door-to-Door (road)'],
  ocean: ['FCL', 'LCL', 'Bulk (dry / liquid)', 'Break bulk', 'RoRo', 'Tanker', 'Charter vessel', 'NVOCC house B/L'],
  rail: ['Full train / block train', 'Single wagon load', 'Container on flat car (COFC)', 'Conventional rail wagon', 'Intermodal rail leg'],
  air: ['Express air cargo', 'Standard air freight', 'Air charter', 'Courier / integrator', 'ULD / loose load'],
  multimodal: ['Intermodal (sea + road)', 'Door-to-Door', 'Sea-Air', 'Road-Rail', 'FTL + FCL', 'Barge + truck'],
};

/** Safe list for a mode id (lowercase keys). */
export function getShipmentTypesForTransportMode(modeId) {
  const key = String(modeId || 'road').toLowerCase();
  return SHIPMENT_TYPES_BY_TRANSPORT_MODE[key] || SHIPMENT_TYPES_BY_TRANSPORT_MODE.road;
}

export const COMMODITY_TYPE_OPTIONS = [
  'General cargo',
  'Dry bulk',
  'Liquid bulk',
  'Reefer / temperature controlled',
  'Hazardous goods',
  'Project / OOG',
  'Pharma / healthcare',
  'Electronics',
  'Automotive parts',
  'FMCG / retail',
  'Machinery',
  'Live animals',
  'Other',
];

/** Today's date for `<input type="date" />` (local calendar date, YYYY-MM-DD). */
export function getTodayRfqDateString() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Unique RFQ reference for new forms (e.g. RFQ-20260417-143052-A3F9). */
export function generateRfqReferenceNumber() {
  const d = new Date();
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `RFQ-${y}${mo}${day}-${h}${mi}${s}-${rand}`;
}

export function emptyPackageRow() {
  return {
    id: `pkg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    packageNumber: '',
    length: '',
    width: '',
    height: '',
    weightKg: '',
    quantity: '',
  };
}

export function emptyDocumentRow() {
  return {
    id: `doc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    fileName: '',
    sizeLabel: '',
    file: null,
  };
}

export function getInitialGenericRfqForm(companyId) {
  return {
    companyId: companyId != null && Number.isFinite(Number(companyId)) ? Number(companyId) : null,
    // 1 — Reference & company (RFQ Reference & Company Information)
    rfqReferenceNumber: generateRfqReferenceNumber(),
    rfqDate: getTodayRfqDateString(),
    rfqStatus: 'draft',
    companyName: '',
    industry: '',
    contactPerson: '',
    contactNumber: '',
    email: '',
    companyAddress: '',
    companyWebsite: '',
    // 2 — Transport (modeDetail = shipment type; options depend on transportMode)
    transportMode: 'road',
    modeDetail: '',
    commodityType: '',
    hsCode: '',
    // 3 — Origin (country / state / city = master IDs as string, same APIs as company profile)
    originAddress: '',
    originCountry: '',
    originState: '',
    originCity: '',
    originZip: '',
    originContactName: '',
    originContactNumber: '',
    // 4 — Destination (country / state / city = master IDs as string)
    destAddress: '',
    destCountry: '',
    destState: '',
    destCity: '',
    destZip: '',
    destContactName: '',
    destContactNumber: '',
    // 5 — Cargo (details & specifications — metrics + package dims + characteristics + special toggles)
    cargoDescription: '',
    totalUnits: '',
    totalWeightValue: '',
    totalWeightUnit: 'KG',
    totalVolumeValue: '',
    totalVolumeUnit: 'CBM',
    cargoValue: '',
    cargoValueCurrency: 'INR',
    packages: [emptyPackageRow()],
    charGeneralCargo: false,
    charOversized: false,
    charPerishable: false,
    charHeavyMachinery: false,
    charFragile: false,
    charValuableGoods: false,
    charHazardous: false,
    charDocuments: false,
    cargoTemperatureControlled: false,
    specialHazardousMaterial: false,
    specialStackable: false,
    fragileHandling: false,
    unitWeightValue: '',
    unitWeightUnit: 'kg',
    cargoHazardous: false,
    cargoFragile: false,
    cargoPerishable: false,
    requiresLiftgate: false,
    standardDelivery: true,
    oversized: false,
    // 6 — Timeline
    pickupDate: '',
    deliveryDate: '',
    /** Time range (HTML `type="time"` values, HH:mm). */
    pickupTimeFrom: '',
    pickupTimeTo: '',
    deliveryTimeFrom: '',
    deliveryTimeTo: '',
    urgencyLevel: '', // Standard | Express | Urgent
    flexibleDates: false,
    // 7 — Service requirements & additional services
    cargoInsuranceRequired: false,
    customsClearanceSupport: false,
    customsBrokerNeeded: false,
    additionalServices: {},
    loadingEquipmentRequired: false,
    unloadingEquipmentRequired: false,
    appointmentDockRequired: false,
    professionalPackagingRequired: false,
    cargoPalletized: false,
    // 8 — Commercial Terms & Volume
    incoterm: '',
    expectedBudgetAmount: '',
    expectedBudgetCurrency: 'USD',
    quoteDeadline: '',
    quoteValidityPeriod: '',
    creditTerms: '',
    preferredPaymentTerms: [],
    shipmentFrequency: '',
    expectedVolumePerMonth: '',
    annualVolume: '',
    contractDuration: '',
    // 9 — Carrier preferences & documentation
    preferredCarriers: '',
    excludedCarriers: '',
    carrierRequirements: '',
    docCommercialInvoice: false,
    docPackingList: false,
    docMsds: false,
    documents: [emptyDocumentRow()],
    // 10 — Special instructions & terms acceptance
    specialInstructionsComments: '',
    complianceRequirements: '',
    sustainabilityPreferences: '',
    returnLogisticsRequired: false,
    termsAccepted: false,
  };
}
